/** Строка реестра приложения №1 (таблица в приложении к акту). */
export type AppendixRegistryRow = {
  material: string
  docName: string
  docNo: string
  docDate: string
  sheets: string
}

export function createEmptyAppendixRegistryRow(): AppendixRegistryRow {
  return { material: '', docName: '', docNo: '', docDate: '', sheets: '' }
}

export const APPENDIX_REGISTRY_ATTR = 'data-appendix-registry'
export const APPENDIX_REGISTRY_PAYLOAD_ATTR = 'data-appendix-registry-payload'
export const APPENDIX_REGISTRY_ROW_ATTR = 'data-appendix-registry-row'
export const APPENDIX_REGISTRY_DISPLAY_ATTR = 'data-appendix-registry-display'
export const APPENDIX_SECTION_ATTR = 'data-act-appendix'
export const APPENDIX_ROW_THRESHOLD = 5
export const APPENDIX_MAIN_FIELD_TEXT = 'согласно реестра приложений №1'

export const APPENDIX_SYNC_OBJECT_VAR = 'data-appendix-sync-object-var'
export const APPENDIX_SYNC_BUILDER_VAR = 'data-appendix-sync-builder-var'
export const APPENDIX_SYNC_ACT_NO_VAR = 'data-appendix-sync-act-no-var'
export const APPENDIX_SYNC_ACT_DAY_VAR = 'data-appendix-sync-act-day-var'
export const APPENDIX_SYNC_ACT_MONTH_VAR = 'data-appendix-sync-act-month-var'
export const APPENDIX_SYNC_ACT_YEAR_VAR = 'data-appendix-sync-act-year-var'

const SYNC_OBJECT = APPENDIX_SYNC_OBJECT_VAR
const SYNC_BUILDER = APPENDIX_SYNC_BUILDER_VAR
const SYNC_ACT_NO = APPENDIX_SYNC_ACT_NO_VAR
const SYNC_ACT_DAY = APPENDIX_SYNC_ACT_DAY_VAR
const SYNC_ACT_MONTH = APPENDIX_SYNC_ACT_MONTH_VAR
const SYNC_ACT_YEAR = APPENDIX_SYNC_ACT_YEAR_VAR

const SLOT_OBJECT = 'data-appendix-sync-slot="object"'
const SLOT_BUILDER = 'data-appendix-sync-slot="builder"'
const SLOT_ACT_NO = 'data-appendix-sync-slot="actNo"'
const SLOT_ACT_DAY = 'data-appendix-sync-slot="actDay"'
const SLOT_ACT_MONTH = 'data-appendix-sync-slot="actMonth"'
const SLOT_ACT_YEAR = 'data-appendix-sync-slot="actYear"'

/** Номер документа: без ведущих спецсимволов (№ подставляется при форматировании). */
export function sanitizeAppendixDocNo(value: string): string {
  return String(value ?? '').replace(/^[^\p{L}\p{N}]+/u, '')
}

/** Кол-во листов: только цифры (суффикс «л.» подставляется при форматировании). */
export function sanitizeAppendixSheets(value: string): string {
  return String(value ?? '').replace(/\D/g, '')
}

function normalizeRow(r: Partial<AppendixRegistryRow>): AppendixRegistryRow {
  return {
    material: String(r.material ?? ''),
    docName: String(r.docName ?? ''),
    docNo: sanitizeAppendixDocNo(String(r.docNo ?? '')),
    docDate: String(r.docDate ?? ''),
    sheets: sanitizeAppendixSheets(String(r.sheets ?? ''))
  }
}

export function encodeRegistryPayload(rows: AppendixRegistryRow[]): string {
  try {
    const json = JSON.stringify(rows)
    return btoa(unescape(encodeURIComponent(json)))
  } catch {
    return ''
  }
}

export function decodeRegistryPayload(raw: string | null | undefined): AppendixRegistryRow[] {
  if (!raw || !String(raw).trim()) return []
  try {
    const json = decodeURIComponent(escape(atob(String(raw).trim())))
    const data = JSON.parse(json)
    if (!Array.isArray(data)) return []
    return data.map((x) => normalizeRow(x))
  } catch {
    return []
  }
}

/** Убирает полностью пустые строки перед сохранением. */
export function compactRegistryRows(rows: AppendixRegistryRow[]): AppendixRegistryRow[] {
  return rows.filter((r) =>
    [r.material, r.docName, r.docNo, r.docDate, r.sheets].some((x) => String(x ?? '').trim())
  )
}

export function formatRegistryMainFieldLine(row: AppendixRegistryRow, index: number): string {
  return `${index + 1}. ${row.material}, ${row.docName}, № ${row.docNo}, ${row.docDate}, ${row.sheets} л.`
}

export function formatRegistryMainFieldText(rows: AppendixRegistryRow[]): string {
  const n = rows.length
  if (n === 0) return ''
  if (n > APPENDIX_ROW_THRESHOLD) return APPENDIX_MAIN_FIELD_TEXT
  return rows.map((r, i) => formatRegistryMainFieldLine(r, i)).join('\n')
}

/** Заполняет текстовое поле (fallback, если нет строки-шаблона таблицы). */
export function setRegistryMainFieldContent(trigger: HTMLElement, rows: AppendixRegistryRow[]): void {
  const n = rows.length
  if (n === 0) {
    trigger.innerHTML = '&nbsp;'
    return
  }
  if (n > APPENDIX_ROW_THRESHOLD) {
    trigger.textContent = APPENDIX_MAIN_FIELD_TEXT
    return
  }
  trigger.textContent = formatRegistryMainFieldLine(rows[0], 0)
}

function setDisplayOnlyText(el: HTMLElement, text: string): void {
  if (!text.trim()) {
    el.innerHTML = '&nbsp;'
    return
  }
  el.textContent = text
}

function findRegistryTemplateRow(trigger: HTMLElement): HTMLTableRowElement | null {
  const inTemplate = trigger.closest('tr[data-row-template]')
  if (inTemplate) return inTemplate as HTMLTableRowElement
  return trigger.closest('tr')
}

/** Точка вставки новых строк — как у кнопки «+» (data-row-template-after-row + data-removable). */
function getRegistryInsertAnchor(templateRow: HTMLTableRowElement): HTMLTableRowElement {
  const afterRows = Math.max(0, parseInt(templateRow.getAttribute('data-row-template-after-row') || '0', 10))
  let anchor: Element | null = templateRow
  for (let i = 0; i < afterRows && anchor?.nextElementSibling; i++) {
    anchor = anchor.nextElementSibling
  }
  let insertAfter: Element | null = anchor
  let nextRow = insertAfter?.nextElementSibling ?? null
  while (nextRow instanceof HTMLTableRowElement && nextRow.hasAttribute('data-removable')) {
    insertAfter = nextRow
    nextRow = insertAfter.nextElementSibling
  }
  return insertAfter as HTMLTableRowElement
}

function getRowAfterTemplateOffset(templateRow: HTMLTableRowElement): Element | null {
  const afterRows = Math.max(0, parseInt(templateRow.getAttribute('data-row-template-after-row') || '0', 10))
  let current: Element | null = templateRow
  for (let i = 0; i < afterRows && current?.nextElementSibling; i++) {
    current = current.nextElementSibling
  }
  return current
}

function collectRegistryContinuationRows(
  templateRow: HTMLTableRowElement,
  registryId: string
): HTMLTableRowElement[] {
  const rows: HTMLTableRowElement[] = []
  let next = getRowAfterTemplateOffset(templateRow)?.nextElementSibling ?? null
  while (
    next instanceof HTMLTableRowElement &&
    next.getAttribute(APPENDIX_REGISTRY_ROW_ATTR) === registryId
  ) {
    rows.push(next)
    next = next.nextElementSibling
  }
  return rows
}

function getContentTdIndex(trigger: HTMLElement): number {
  const triggerTd = trigger.closest('td')
  if (!triggerTd) return 0
  const row = triggerTd.closest('tr')
  if (!row) return 0
  return Array.from(row.querySelectorAll('td')).indexOf(triggerTd as HTMLTableCellElement)
}

function clearLabelCellsInContinuationRow(cloned: HTMLTableRowElement, contentTdIndex: number): void {
  const tds = Array.from(cloned.querySelectorAll('td'))
  for (let i = 0; i < contentTdIndex; i++) {
    if (tds[i]) tds[i].innerHTML = '&nbsp;'
  }
}

function cloneRegistryContinuationRow(
  templateRow: HTMLTableRowElement,
  trigger: HTMLElement,
  registryId: string
): HTMLTableRowElement {
  const cloned = templateRow.cloneNode(true) as HTMLTableRowElement
  cloned.removeAttribute('data-row-template')
  cloned.setAttribute('data-removable', 'true')
  cloned.setAttribute(APPENDIX_REGISTRY_ROW_ATTR, registryId)

  const registryEl = cloned.querySelector(`[${APPENDIX_REGISTRY_ATTR}]`)
  if (registryEl) {
    const display = document.createElement('span')
    display.setAttribute(APPENDIX_REGISTRY_DISPLAY_ATTR, registryId)
    display.setAttribute('contenteditable', 'false')
    registryEl.replaceWith(display)
  }

  cloned.querySelectorAll<HTMLElement>('[data-editable="true"]').forEach((el) => {
    if (el.hasAttribute(APPENDIX_REGISTRY_DISPLAY_ATTR)) return
    el.textContent = ''
  })

  clearLabelCellsInContinuationRow(cloned, getContentTdIndex(trigger))
  return cloned
}

function insertRegistryContinuationRow(
  templateRow: HTMLTableRowElement,
  trigger: HTMLElement,
  registryId: string
): HTMLTableRowElement {
  const cloned = cloneRegistryContinuationRow(templateRow, trigger, registryId)
  const existing = collectRegistryContinuationRows(templateRow, registryId)
  const anchor = existing.length > 0 ? existing[existing.length - 1] : getRegistryInsertAnchor(templateRow)
  anchor.parentNode?.insertBefore(cloned, anchor.nextSibling)
  return cloned
}

function removeRegistryContinuationRows(rows: HTMLTableRowElement[]): void {
  rows.forEach((row) => row.remove())
}

/**
 * Раскладывает позиции реестра по строкам таблицы акта (как «+» в конструкторе):
 * первая — в строке-шаблоне с подписью, остальные — в клонированных tr.
 */
export function syncRegistryRowsToTemplateTable(
  trigger: HTMLElement,
  rows: AppendixRegistryRow[]
): void {
  const registryId = trigger.getAttribute(APPENDIX_REGISTRY_ATTR) || '1'
  const templateRow = findRegistryTemplateRow(trigger)
  if (!templateRow?.parentNode) {
    setRegistryMainFieldContent(trigger, rows)
    return
  }

  const continuationRows = collectRegistryContinuationRows(templateRow, registryId)
  const n = rows.length

  if (n === 0) {
    setDisplayOnlyText(trigger, '')
    removeRegistryContinuationRows(continuationRows)
    return
  }

  if (n > APPENDIX_ROW_THRESHOLD) {
    setDisplayOnlyText(trigger, APPENDIX_MAIN_FIELD_TEXT)
    removeRegistryContinuationRows(continuationRows)
    return
  }

  setDisplayOnlyText(trigger, formatRegistryMainFieldLine(rows[0], 0))

  const neededContinuation = n - 1
  let currentContinuation = collectRegistryContinuationRows(templateRow, registryId)

  while (currentContinuation.length > neededContinuation) {
    currentContinuation.pop()?.remove()
    currentContinuation = collectRegistryContinuationRows(templateRow, registryId)
  }

  while (currentContinuation.length < neededContinuation) {
    insertRegistryContinuationRow(templateRow, trigger, registryId)
    currentContinuation = collectRegistryContinuationRows(templateRow, registryId)
  }

  currentContinuation.forEach((row, i) => {
    const display =
      row.querySelector<HTMLElement>(`[${APPENDIX_REGISTRY_DISPLAY_ATTR}]`) ??
      row.querySelector<HTMLElement>('td')
    if (display) setDisplayOnlyText(display, formatRegistryMainFieldLine(rows[i + 1], i + 1))
  })
}

function getVarText(root: HTMLElement, varKey: string | null | undefined): string {
  if (!varKey || !varKey.trim()) return ''
  const el = root.querySelector<HTMLElement>(`[data-var="${CSS.escape(varKey.trim())}"]`)
  return (el?.textContent || '').trim()
}

function setSlotText(section: HTMLElement, slotSelector: string, text: string) {
  const span = section.querySelector<HTMLElement>(`span[${slotSelector}]`)
  if (span) span.textContent = text
}

export function removeAppendixSection(root: HTMLElement, appendixId: string) {
  root.querySelector(`section[${APPENDIX_SECTION_ATTR}="${CSS.escape(appendixId)}"]`)?.remove()
}

function appendixSectionTemplate(appendixId: string): string {
  return `
<section ${APPENDIX_SECTION_ATTR}="${appendixId}" style="page-break-before:always; font-size: 10pt">
  <table cellspacing="0" cellpadding="0" style="width: 100%; border-collapse: collapse">
    <tbody>
        <tr style="height: 14.25pt; font-weight:bold;">
            <td colspan="20" style="width: 497.1pt; vertical-align: bottom">
                <p style="margin-top: 0; margin-bottom: 0; font-size: 10pt">Объект капитального строительства</p>
            </td>
        </tr>
        <tr style="height: 12pt; position: relative">
            <td colspan="20" style="width: 497.1pt; border-bottom-style: solid; vertical-align: middle">
                <p style="margin-top: 0; margin-bottom: 0; text-align: center; font-size: 10pt"><strong><em><span ${SLOT_OBJECT}></span></em></strong></p>
            </td>
        </tr>
    </tbody>
  </table>
  <p style="text-align:center;margin:1rem 0; font-weight:bold;">
    <strong>Реестр приложений № 1<br/>
    к Акту освидетельствования скрытых работ
    </strong>
    <br/>
    № <span ${SLOT_ACT_NO}></span> от «<span ${SLOT_ACT_DAY}></span>» <span ${SLOT_ACT_MONTH}></span> <span ${SLOT_ACT_YEAR}></span> г.
  </p>
  <table border="1" cellspacing="0" cellpadding="6" style="width:100%;border-collapse:collapse; font-size: 10pt;">
    <thead>
      <tr>
        <th style="width:4%; text-align:center;">№ п/п</th>
        <th style="width:22%; text-align:center;">Наименование материала</th>
        <th style="width:22%; text-align:center;">Наименование документа</th>
        <th style="width:12%; text-align:center;">№ документа</th>
        <th style="width:14%; text-align:center;">Дата документа</th>
        <th style="width:10%; text-align:center;">Кол-во листов</th>
      </tr>
    </thead>
    <tbody data-appendix-registry-tbody></tbody>
  </table>
  <table cellspacing="0" cellpadding="0" style="width: 100%; border-collapse: collapse; margin-top: 1rem;">
    <tbody>
      <tr style="height: 12pt">
                <td colspan="20" style="width: 497.1pt; vertical-align: bottom; font-weight:bold;">
                    <p style="margin-top: 0; margin-bottom: 0; font-size: 10pt">Представитель лица, осуществляющего строительство, реконструкцию, капитальный ремонт</p>
                </td>
            </tr>
            <tr style="height: 12pt; position: relative">
                <td colspan="20" style="width: 497.1pt; border-bottom-style: solid; border-bottom-width: 0.75pt; vertical-align: middle">
                    <p style="margin-top: 0; margin-bottom: 0; font-size: 10pt"><strong><em><span ${SLOT_BUILDER}></span></em></strong></p>
                </td>
            </tr>
            <tr style="height: 9pt">
                <td colspan="20" style="width: 497.1pt; vertical-align: middle">
                    <p style="margin-top: 0; margin-bottom: 0; text-align: center; font-size: 6pt">(должность, фамилия, инициалы, подпись)</p>
                </td>
            </tr>
      </tbody>
    </table>
</section>`.trim()
}

function ensureAppendixSection(root: HTMLElement, appendixId: string): HTMLElement {
  const sel = `section[${APPENDIX_SECTION_ATTR}="${CSS.escape(appendixId)}"]`
  let sec = root.querySelector<HTMLElement>(sel)
  if (!sec) {
    const wrap = document.createElement('div')
    wrap.innerHTML = appendixSectionTemplate(appendixId)
    sec = wrap.firstElementChild as HTMLElement
    root.appendChild(sec)
  }
  return sec
}

function fillRegistryTableBody(section: HTMLElement, rows: AppendixRegistryRow[]) {
  const tbody = section.querySelector<HTMLTableSectionElement>('tbody[data-appendix-registry-tbody]')
  if (!tbody) return
  tbody.innerHTML = ''
  rows.forEach((r, idx) => {
    const tr = document.createElement('tr')
    tr.innerHTML = [
      `<td style="text-align:center;">${idx + 1}</td>`,
      `<td>${escapeHtmlCell(r.material)}</td>`,
      `<td>${escapeHtmlCell(r.docName)}</td>`,
      `<td>${escapeHtmlCell(r.docNo)}</td>`,
      `<td>${escapeHtmlCell(r.docDate)}</td>`,
      `<td style="text-align:center;">${escapeHtmlCell(r.sheets)}</td>`
    ].join('')
    tbody.appendChild(tr)
  })
}

function escapeHtmlCell(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function copySyncConfigFromTrigger(section: HTMLElement, trigger: HTMLElement) {
  const o = trigger.getAttribute(SYNC_OBJECT)
  const b = trigger.getAttribute(SYNC_BUILDER)
  const n = trigger.getAttribute(SYNC_ACT_NO)
  const day = trigger.getAttribute(SYNC_ACT_DAY)
  const month = trigger.getAttribute(SYNC_ACT_MONTH)
  const year = trigger.getAttribute(SYNC_ACT_YEAR)
  if (o) section.setAttribute('data-appendix-src-object-var', o)
  else section.removeAttribute('data-appendix-src-object-var')
  if (b) section.setAttribute('data-appendix-src-builder-var', b)
  else section.removeAttribute('data-appendix-src-builder-var')
  if (n) section.setAttribute('data-appendix-src-actno-var', n)
  else section.removeAttribute('data-appendix-src-actno-var')
  if (day) section.setAttribute('data-appendix-src-actday-var', day)
  else section.removeAttribute('data-appendix-src-actday-var')
  if (month) section.setAttribute('data-appendix-src-actmonth-var', month)
  else section.removeAttribute('data-appendix-src-actmonth-var')
  if (year) section.setAttribute('data-appendix-src-actyear-var', year)
  else section.removeAttribute('data-appendix-src-actyear-var')
  section.removeAttribute('data-appendix-src-actdate-var')
}

function applySyncSlots(root: HTMLElement, section: HTMLElement) {
  const objVar = section.getAttribute('data-appendix-src-object-var')
  const bVar = section.getAttribute('data-appendix-src-builder-var')
  const noVar = section.getAttribute('data-appendix-src-actno-var')
  const dayVar = section.getAttribute('data-appendix-src-actday-var')
  const monthVar = section.getAttribute('data-appendix-src-actmonth-var')
  const yearVar = section.getAttribute('data-appendix-src-actyear-var')
  setSlotText(section, SLOT_OBJECT, getVarText(root, objVar))
  setSlotText(section, SLOT_BUILDER, getVarText(root, bVar))
  setSlotText(section, SLOT_ACT_NO, getVarText(root, noVar))
  setSlotText(section, SLOT_ACT_DAY, getVarText(root, dayVar))
  setSlotText(section, SLOT_ACT_MONTH, getVarText(root, monthVar))
  setSlotText(section, SLOT_ACT_YEAR, getVarText(root, yearVar))
}

/**
 * Обновляет блок приложения и поле в основном акте по данным реестра.
 * Вызывать после правок в модалке и перед выгрузкой HTML (getCurrentHtml).
 */
export function syncAppendixRegistryForRoot(root: HTMLElement) {
  const triggers = root.querySelectorAll<HTMLElement>(`[${APPENDIX_REGISTRY_ATTR}]`)
  triggers.forEach((trigger) => {
    const appendixId = trigger.getAttribute(APPENDIX_REGISTRY_ATTR) || '1'
    const rows = decodeRegistryPayload(trigger.getAttribute(APPENDIX_REGISTRY_PAYLOAD_ATTR))
    syncRegistryRowsToTemplateTable(trigger, rows)

    if (rows.length > APPENDIX_ROW_THRESHOLD) {
      const section = ensureAppendixSection(root, appendixId)
      copySyncConfigFromTrigger(section, trigger)
      fillRegistryTableBody(section, rows)
      applySyncSlots(root, section)
    } else {
      removeAppendixSection(root, appendixId)
    }
  })
}

export function isAppendixRegistryTrigger(el: HTMLElement | null): boolean {
  return !!el?.hasAttribute(APPENDIX_REGISTRY_ATTR)
}
